import { useCallback, useEffect, useRef } from 'react';
import type { B24Frame } from '@bitrix24/b24jssdk';
import { useB24Frame } from '@/contexts/B24FrameContext';
import { scrollParentToElement } from '@/lib/embedded-content-sized';
import { frameUsesHostMessages } from '@/lib/embedded-host';
import { logDebug } from '@/lib/logger';

function scrollParent(frame: B24Frame, element: Element): void {
  scrollParentToElement(frame.parent, element).catch((error: unknown) => {
    logDebug('embedded.scroll_parent_failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  });
}

/**
 * Scroll the Bitrix24 page to an element of this frame (#2679). The function is
 * stable.
 *
 * Without a frame the window of this document is the scroller (the screen open in
 * a browser tab, not inside Bitrix24), and `scrollIntoView` does it. The frame
 * may also be only late — a cookie session renders a deep-linked record before
 * the SDK has initialised — so the request also waits for it and goes then,
 * unless the element has left the page or a newer request replaced it.
 *
 * The page of a message host (Uspacy) is not scrolled from the frame at all
 * (`2026-10-29-uspacy-card-tab-session.md` §10.2): the frame is as tall as its
 * content, the page owns the scroll, and `scrollIntoView` would move every
 * ancestor frame. The rule lives here, not at the call sites, so that no screen
 * can forget it.
 */
export function useScrollParentTo(): (element: Element) => void {
  const { b24Frame } = useB24Frame();
  const frameRef = useRef(b24Frame);
  const waiting = useRef<Element | null>(null);
  useEffect(() => {
    frameRef.current = b24Frame;
    // Only a frame that is there takes the waiting request: the first run of
    // the effect, with the frame still on its way, must leave it queued.
    if (!b24Frame) return;
    const element = waiting.current;
    waiting.current = null;
    if (element?.isConnected) scrollParent(b24Frame, element);
  }, [b24Frame]);
  return useCallback((element: Element) => {
    if (frameUsesHostMessages()) return;
    const frame = frameRef.current;
    if (!frame) {
      waiting.current = element;
      element.scrollIntoView({ block: 'start' });
      return;
    }
    waiting.current = null;
    scrollParent(frame, element);
  }, []);
}
